import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@foris/avocado-suite";
import { IParams } from "@models/IParams";
import { useRejectAssignmentProposal } from "@services/collections/assignment-proposal/assignmentProposalServices";
import { outcomeErrorKey } from "@utils/assignmentProposal";
import { useActiveProposal } from "./useActiveProposal";
import { useLeaveDecidedProposal } from "./useLeaveDecidedProposal";

/**
 * The rejection of the proposal under review: its modal and its request. A rejection that goes
 * through leaves the editor; a failed one keeps the modal open with the message.
 */
export const useRejectActiveProposal = () => {
  const { scenario, origin }: IParams = useParams();
  const { t } = useTranslation();
  const activeProposal = useActiveProposal();
  const { submit, isSubmitting } = useRejectAssignmentProposal();
  const leaveDecidedProposal = useLeaveDecidedProposal();
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const open = useCallback(() => {
    setErrorMessage(null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const reject = async (reason: string) => {
    if (!activeProposal) return;

    setErrorMessage(null);
    const outcome = await submit({
      scenarioId: scenario,
      originId: origin,
      linkId: activeProposal.linkId,
      proposalId: activeProposal.proposalId,
      reason,
    });

    if (!outcome) return;

    if (outcome.kind === "recorded") {
      setIsOpen(false);
      toast.success(t("assignation-edit.proposal.toast.rejected"), { autoClose: 3000 });
      leaveDecidedProposal();
      return;
    }

    setErrorMessage(t(outcomeErrorKey(outcome, "reject")));
  };

  return { isOpen, open, close, reject, isSubmitting, errorMessage };
};
